import { HttpClient, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { decodeRegionFromId, formatDate } from '@app/shared/helpers/helpers';
import { Preview } from '@app/shared/models/preview';
import { Transfer } from '@app/shared/models/transfer';
import { TransferFile } from '@app/shared/models/transferFile';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

const apiTransferVersion = '10-2019';
const apiTransferVersion012024 = '01-2024';

@Injectable({
  providedIn: 'root'
})
export class TransferService {

  headers = new HttpHeaders().set('Authorization', 'true');

  getHeadersWithPassword(password) {
    return new HttpHeaders({
      Authorization: 'true',
      'smash-authorization': `${encodeURIComponent(password)}`
    });
  }

  constructor(private http: HttpClient, private translateService: TranslateService) { }

  listTransfersSent(id: string): Observable<{ transfers: Transfer[] }> {
    return this.http.get<{ transfers: Transfer[] }>('service:transfer' + '/user/' + id + '/transfers' + '?version=' + apiTransferVersion, { headers: this.headers });
  }

  listTransfersReceived(id: string): Observable<{ transfers: Transfer[] }> {
    return this.http.get<{ transfers: Transfer[] }>('service:transfer' + '/user/' + id + '/transfers' + '?version=' + apiTransferVersion, { headers: this.headers });
  }

  listTransfersByAccount(): Observable<{ transfers: Transfer[] }> {
    return this.http.get<{ transfers: Transfer[] }>('service:transfer' + '/transfers' + '?version=' + apiTransferVersion012024, { headers: this.headers });
  }

  getTransferByAccount(transferId: string): Observable<{ transfer: Transfer }> {
    const region = decodeRegionFromId(transferId);
    if (region) {
      return this.http.get<{ transfer: Transfer }>('https://' + environment.prefixUrl + 'transfer.' + region + '.fromsmash.co' + '/transfer/' + transferId + '?version=' + apiTransferVersion, { headers: this.headers });
    } else {
      return this.http.get<{ transfer: Transfer }>('service:transfer' + '/transfer/' + transferId + '?version=' + apiTransferVersion, { headers: this.headers });
    }
  }

  getAccountTransferEvents(id: string): Observable<any[]> {
    const region = decodeRegionFromId(id);
    if (region) {
      return this.http.get<any>('https://' + environment.prefixUrl + 'transfer.' + region + '.fromsmash.co' + '/transfer/' + id + '/events' + '?version=' + apiTransferVersion, { headers: this.headers });
    } else {
      return this.http.get<any>('service:transfer' + '/transfer/' + id + '/events' + '?version=' + apiTransferVersion, { headers: this.headers });
    }
  }

  inactivateTransfer(transferId: string) {
    const region = decodeRegionFromId(transferId);
    if (region) {
      return this.http.delete('https://' + environment.prefixUrl + 'transfer.' + region + '.fromsmash.co' + '/transfer/' + transferId + '?version=' + apiTransferVersion, { headers: this.headers });
    } else {
      return this.http.delete('service:transfer' + '/transfer/' + transferId + '?version=' + apiTransferVersion, { headers: this.headers });
    }
  }

  // TO FIX : wait for api endpoint
  getTransfersByUser(user: string): Observable<Transfer> {
    return this.http.get<Transfer>('service:transfer' + '/user/' + user + '/transfers' + '?version=' + apiTransferVersion, { headers: this.headers });
  }

  getTeamTransfersByUser(teamId: string, user: string): Observable<Transfer> {
    return this.http.get<Transfer>('service:transfer' + '/team/' + teamId + '/user/' + user + '/transfers' + '?version=' + apiTransferVersion012024, { headers: this.headers });
  }

  // need root access
  getTransfersByTeam(team: string): Observable<Transfer> {
    return this.http.get<Transfer>('service:transfer' + '/team/' + team + '/transfers' + '?version=' + apiTransferVersion, { headers: this.headers });
  }

  getTransferByUser(user: string, transferId: string): Observable<Transfer> {
    return this.http.get<Transfer>('service:transfer' + '/user/' + user + '/transfer/' + transferId + '?version=' + apiTransferVersion, { headers: this.headers });
  }

  getTransferByTeam(transferId: string, teamId: string): Observable<Transfer> {
    const region = decodeRegionFromId(transferId);
    if (region) {
      return this.http.get<Transfer>('https://' + environment.prefixUrl + 'transfer.' + region + '.fromsmash.co' + '/team/' + teamId + '/transfer/' + transferId + '?version=' + apiTransferVersion, { headers: this.headers });
    } else {
      return this.http.get<Transfer>('service:transfer' + '/team/' + teamId + '/transfer/' + transferId + '?version=' + apiTransferVersion, { headers: this.headers });
    }
  }

  getTransferFile(transferId: string, fileId: string): Observable<{ file: any }> {
    const region = decodeRegionFromId(transferId);
    if (region) {
      return this.http.get<any>('https://' + environment.prefixUrl + 'transfer.' + region + '.fromsmash.co' + '/transfer/' + transferId + '/file/' + fileId + '?version=' + apiTransferVersion, { headers: this.headers });
    } else {
      return this.http.get<any>('service:transfer' + '/transfer/' + transferId + '/file/' + fileId + '?version=' + apiTransferVersion, { headers: this.headers });
    }
  }

  getTransferFiles(id: string): Observable<Transfer> {
    const region = decodeRegionFromId(id);
    if (region) {
      return this.http.get<any>('https://' + environment.prefixUrl + 'transfer.' + region + '.fromsmash.co' + '/transfer/' + id + '/files' + '?version=' + apiTransferVersion, { headers: this.headers });
    } else {
      return this.http.get<any>('service:transfer' + '/transfer/' + id + '/files' + '?version=' + apiTransferVersion, { headers: this.headers });
    }
  }

  getTeamTransferFiles(id: string, teamId: string): Observable<Transfer> {
    const region = decodeRegionFromId(id);
    if (region) {
      return this.http.get<any>('https://' + environment.prefixUrl + 'transfer.' + region + '.fromsmash.co' + '/team/' + teamId + '/transfer/' + id + '/files' + '?version=' + apiTransferVersion, { headers: this.headers });
    } else {
      return this.http.get<any>('service:transfer' + '/team/' + teamId + '/transfer/' + id + '/files' + '?version=' + apiTransferVersion, { headers: this.headers });
    }
  }

  getUserTransferFiles(userId: string, transferId: string): Observable<Transfer> {
    const region = decodeRegionFromId(userId);
    if (region) {
      return this.http.get<any>('https://' + environment.prefixUrl + 'transfer.' + region + '.fromsmash.co' + '/user/' + userId + '/transfer/' + transferId + '/files' + '?version=' + apiTransferVersion, { headers: this.headers });
    } else {
      return this.http.get<any>('service:transfer' + '/user/' + userId + '/transfer/' + transferId + '/files' + '?version=' + apiTransferVersion, { headers: this.headers });
    }
  }

  getTeamTransferEvents(id: string, teamId: string): Observable<any[]> {
    const region = decodeRegionFromId(id);
    if (region) {
      return this.http.get<any>('https://' + environment.prefixUrl + 'transfer.' + region + '.fromsmash.co' + '/team/' + teamId + '/transfer/' + id + '/events' + '?version=' + apiTransferVersion, { headers: this.headers });
    } else {
      return this.http.get<any>('service:transfer' + '/team/' + teamId + '/transfer/' + id + '/events' + '?version=' + apiTransferVersion, { headers: this.headers });
    }
  }

  getUserTransferEvents(transferId: string, userId: string,): Observable<any[]> {
    const region = decodeRegionFromId(userId);
    if (region) {
      return this.http.get<any>('https://' + environment.prefixUrl + 'transfer.' + region + '.fromsmash.co' + '/user/' + userId + '/transfer/' + transferId + '/events' + '?version=' + apiTransferVersion, { headers: this.headers });
    } else {
      return this.http.get<any>('service:transfer' + '/user/' + userId + '/transfer/' + transferId + '/events' + '?version=' + apiTransferVersion, { headers: this.headers });
    }
  }

  deleteTransfer(id: string): Observable<Transfer> {
    return this.http.delete<Transfer>('service:transfer' + '/report/' + id + '?version=' + apiTransferVersion, { headers: this.headers });
  }

  inactivateTeamTransfer(transferId: string, teamdId: string) {
    const region = decodeRegionFromId(transferId);
    if (region) {
      return this.http.delete('https://' + environment.prefixUrl + 'transfer.' + region + '.fromsmash.co' + '/team/' + teamdId + '/transfer/' + transferId + '?version=' + apiTransferVersion, { headers: this.headers });
    } else {
      return this.http.delete('service:transfer' + '/team/' + teamdId + '/transfer/' + transferId + '?version=' + apiTransferVersion, { headers: this.headers });
    }
  }

  inactivateUserTransfer(transferId: string, userId: string) {
    const region = decodeRegionFromId(transferId);
    if (region) {
      return this.http.delete('https://' + environment.prefixUrl + 'transfer.' + region + '.fromsmash.co' + '/user/' + userId + '/transfer/' + transferId + '?version=' + apiTransferVersion, { headers: this.headers });
    } else {
      return this.http.delete('service:transfer' + '/user/' + userId + '/transfer/' + transferId + '?version=' + apiTransferVersion, { headers: this.headers });
    }
  }

  getPreview(id: string, password?: string): Observable<Preview> {
    if (!!password) {
      return this.http.get<Preview>('service:transfer' + '/transfer/' + id + '/preview' + '?version=' + apiTransferVersion, { headers: this.getHeadersWithPassword(password) });
    }
    return this.http.get<Preview>('service:transfer' + '/transfer/' + id + '/preview' + '?version=' + apiTransferVersion, { headers: this.headers });
  }

  getPreviewByTarget(target: string, password?: string, queryParams?: string): Observable<Preview> {
    if (!!password) {
      return this.http.get<Preview>(target + '?version=07-2020' + queryParams, { headers: this.getHeadersWithPassword(password) });
    }
    return this.http.get<Preview>(target + '?version=07-2020' + queryParams, { headers: this.headers });
  }

  getReport(reportId: string): Observable<any> {
    const region = decodeRegionFromId(reportId);
    if (region) {
      return this.http.get<any>('https://' + environment.prefixUrl + 'transfer.' + region + '.fromsmash.co' + '/report/' + reportId + '?version=' + apiTransferVersion, { headers: this.headers });
    } else {
      return this.http.get<any>('service:transfer' + '/report/' + reportId + '?version=' + apiTransferVersion, { headers: this.headers });
    }
  }

  deleteReport(reportId: string): Observable<any> {
    const region = decodeRegionFromId(reportId);
    if (region) {
      return this.http.delete<any>('https://' + environment.prefixUrl + 'transfer.' + region + '.fromsmash.co' + '/report/' + reportId + '?version=' + apiTransferVersion, { headers: this.headers });
    } else {
      return this.http.delete('service:transfer' + '/report/' + reportId + '?version=' + apiTransferVersion, { headers: this.headers });
    }
  }

  listMetricStatistics(metricName: string, statisticName: string, startDate: string, endDate: string, unit: string): Observable<any> {
    return this.http.get<any>('service:transfer' + `/metric/${metricName}/statistic/${statisticName}?startDate=${startDate}&endDate=${endDate}&unit=${unit}&version=07-2020`, { headers: this.headers }).pipe(map(res => {
      const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
      res.statistic.data = res.statistic.data.map(counter => ({ ...counter, date: formatDate(lang, counter.date, unit) }));
      return res;
    }));
  }

  listTeamMetricStatistics(metricName: string, statisticName: string, teamId: string, startDate: string, endDate: string, unit: string): Observable<any> {
    return this.http.get<any>('service:transfer' + `/team/${teamId}/metric/${metricName}/statistic/${statisticName}?startDate=${startDate}&endDate=${endDate}&unit=${unit}&version=07-2020`, { headers: this.headers }).pipe(map(res => {
      const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
      res.statistic.data = res.statistic.data.map(counter => ({ ...counter, date: formatDate(lang, counter.date, unit) }));
      return res;
    }));
  }

  listTeamUserMetricStatistics(metricName: string, statisticName: string, teamId: string, userId: string, startDate: string, endDate: string, unit: string): Observable<any> {
    return this.http.get<any>('service:transfer' + `/team/${teamId}/user/${userId}/metric/${metricName}/statistic/${statisticName}?startDate=${startDate}&endDate=${endDate}&unit=${unit}&version=07-2020`, { headers: this.headers }).pipe(map(res => {
      const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
      res.statistic.data = res.statistic.data.map(counter => ({ ...counter, date: formatDate(lang, counter.date, unit) }));
      return res;
    }));
  }

  listUserMetricStatistics(metricName: string, statisticName: string, userId: string, startDate: string, endDate: string, unit: string): Observable<any> {
    return this.http.get<any>('service:transfer' + `/user/${userId}/metric/${metricName}/statistic/${statisticName}?startDate=${startDate}&endDate=${endDate}&unit=${unit}&version=07-2020`, { headers: this.headers }).pipe(map(res => {
      const lang = !!this.translateService.currentLang ? this.translateService.currentLang : this.translateService.defaultLang;
      res.statistic.data = res.statistic.data.map(item => ({ ...item, date: formatDate(lang, item.date, unit) }));
      return res;
    }));
  }

  listTransferFilesPreview(transfer: string, limit: number = 9, queryParams: string, password?: string): Observable<{ files: TransferFile[], next: string, urlsExpiredAt: string }> {
    const region = decodeRegionFromId(transfer);
    if (region) {
      return this.http.get<any>('https://' + environment.prefixUrl + 'transfer.' + region + '.fromsmash.co' + '/transfer/' + transfer + '/files/preview?version=' + '07-2020' + '&limit=' + limit + queryParams, { headers: this.getHeadersWithPassword(password) });
    }
    return this.http.get<any>('service:transfer' + '/transfer/' + transfer + '/files/preview?version=' + '07-2020' + '&limit=' + limit + queryParams, { headers: this.getHeadersWithPassword(password) });
  }

  getMoreFiles(transfer, next = null, limit = 9, queryParams: string, password?: string): Observable<{ files: TransferFile[], next: string, urlsExpiredAt: string }> {
    const region = decodeRegionFromId(transfer);
    if (region) {
      if (next)
        return this.http.get<any>('https://' + environment.prefixUrl + 'transfer.' + region + '.fromsmash.co' + '/transfer/' + transfer + '/files/preview?version=' + '07-2020' + '&start=' + next + '&limit=' + limit + queryParams, { headers: this.getHeadersWithPassword(password) });
      return this.http.get<any>('https://' + environment.prefixUrl + 'transfer.' + region + '.fromsmash.co' + '/transfer/' + transfer + '/files/preview?version=' + '07-2020' + '&limit=' + limit + queryParams, { headers: this.getHeadersWithPassword(password) });
    }
    if (next)
      return this.http.get<any>('service:transfer' + '/transfer/' + transfer + '/files/preview?version=' + '07-2020' + '&start=' + next + '&limit=' + limit + queryParams, { headers: this.getHeadersWithPassword(password) });
    return this.http.get<any>('service:transfer' + '/transfer/' + transfer + '/files/preview?version=' + '07-2020' + '&limit=' + limit + queryParams, { headers: this.getHeadersWithPassword(password) });
  }

  putBatchUrls(transfer: string, queryParams: string, body: { files: { id: string }[] }, password?: string): Observable<{ transfer: { id: string, url: string, files: { id: string, url: string }[] } }> {
    const region = decodeRegionFromId(transfer);
    if (region) {
      return this.http.put<any>('https://' + environment.prefixUrl + 'transfer.' + region + '.fromsmash.co' + '/transfer/' + transfer + '/urls?version=' + '07-2020' + queryParams, body, { headers: this.getHeadersWithPassword(password) });
    }
    return this.http.put<any>('service:transfer' + '/transfer/' + transfer + '/urls?version=07-2020' + queryParams, body, { headers: this.getHeadersWithPassword(password) });
  }
}
